import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ReactiveFormsModule, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { UiKitModule } from '@serviceplan/ui-kit';
import { AssetTypesLocalized, CommonUtilityLib, FieldType, FilterFormDataModel, PROMPTS, ValueListsService, WidthClass } from '@thehubz/client/core';
import { Asset } from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType } from '@thehubz/shared/model';

@Component({
  selector: 'thehubz-asset-filter',
  templateUrl: './asset-filter.component.html',
  styleUrls: ['./asset-filter.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatIconModule, MatFormFieldModule, MatInputModule, MatDatepickerModule, MatAutocompleteModule, UiKitModule],
})
export class AssetFilterComponent implements OnInit {
  @Input() configObject = '';
  @Output() clickApplyFilter = new EventEmitter<Asset>();
  @Output() applyFilterCount = new EventEmitter<number>();

  filterFormData!: FilterFormDataModel;
  filterForm: UntypedFormGroup;
  showHeightWidthFilter = false;

  constructor(
    private _formBuilder: UntypedFormBuilder,
    private valueListsService: ValueListsService,
    private commonUtilityLib: CommonUtilityLib,
  ) {
    this.filterForm = this._formBuilder.group({});
  }

  ngOnInit(): void {
    this.filterFormData = {
      title: $localize`Project`,
      description: '',
      customClient: CustomClients.Standard,
      formFields: [
        {
          formTypeId: '3',
          formFieldName: 'expiryDate',
          fieldType: FieldType.Date,
          fieldLabel: $localize`Valid until`,
          placeholder: $localize`DD/MM/YYYY`,
          inputType: '',
          mandatory: true,
          validationRegex: '',
          widthClass: WidthClass.W12,
          disable: false,
          visible: true,
        },
        {
          formTypeId: '2',
          formFieldName: 'assetType',
          fieldType: FieldType.Dropdown,
          fieldLabel: $localize`Asset type`,
          placeholder: PROMPTS.PLACEHOLDER.DROPDOWN_SELECT_ONE,
          inputType: '',
          mandatory: false,
          validationRegex: '',
          widthClass: this.configObject === 'shareAssets' ? WidthClass.WFull : WidthClass.W12,
          subProjectFlag: false,
          disable: false,
          visible: true,
          options: AssetTypesLocalized,
        },
        {
          formTypeId: '2',
          formFieldName: 'client',
          fieldType: FieldType.Dropdown,
          fieldLabel: $localize`Client`,
          placeholder: PROMPTS.PLACEHOLDER.DROPDOWN_SELECT_ONE,
          inputType: '',
          mandatory: false,
          validationRegex: '',
          widthClass: WidthClass.W12,
          subProjectFlag: false,
          disable: false,
          functionCall: true,
          formGroup: true,
          visible: true,
          options: [],
        },
        {
          formTypeId: '2',
          formFieldName: 'brand',
          fieldType: FieldType.Dropdown,
          fieldLabel: $localize`Brand`,
          placeholder: PROMPTS.PLACEHOLDER.DROPDOWN_SELECT_ONE,
          inputType: '',
          mandatory: false,
          validationRegex: '',
          widthClass: WidthClass.W12,
          disable: false,
          formGroup: true,
          visible: true,
          options: [],
        },
        {
          formTypeId: '2',
          formFieldName: 'status',
          fieldType: FieldType.Dropdown,
          fieldLabel: $localize`Status`,
          placeholder: PROMPTS.PLACEHOLDER.DROPDOWN_SELECT_ONE,
          inputType: '',
          mandatory: false,
          validationRegex: '',
          widthClass: WidthClass.W12,
          subProjectFlag: false,
          disable: false,
          visible: true,
          options: [
            {
              id: Asset.StatusEnum.Uploaded,
              name: $localize`Uploaded`,
            },
            {
              id: Asset.StatusEnum.AwaitingApproval,
              name: $localize`Awaiting approval`,
            },
            {
              id: Asset.StatusEnum.Approved,
              name: $localize`Approved`,
            },
          ],
        },
      ],
    };
    if (this.configObject === 'createProject' || this.configObject === 'createStepper') {
      this.filterFormData.formFields = this.filterFormData.formFields.filter(
        (formField) => formField.formFieldName !== 'client' && formField.formFieldName !== 'brand',
      );
    }
    if (this.configObject === 'shareAssets') {
      this.filterFormData.formFields = this.filterFormData.formFields.filter((formField) => formField.formFieldName === 'assetType');
    }
    if (this.configObject !== 'createProject') {
      this.getCompanies();
    }
    this.filterFormGroup();
  }

  getCompanies() {
    this.valueListsService.listUserClients(['global.library']).subscribe((response) => {
      if (response && response.length > 0) {
        const index = this.commonUtilityLib.findArrayIndex(this.filterFormData, 'client');
        if (index !== -1) {
          this.filterFormData.formFields[index]['options'] = response;
        }
      }
    });
  }

  filterFormGroup() {
    this.filterForm = new UntypedFormGroup({});
    for (const fieldObject of this.filterFormData['formFields']) {
      let fieldValueObject = [];
      if (fieldObject['formFieldName'] && fieldObject['formFieldName'] !== '') {
        const fieldName = fieldObject['formFieldName'];
        if (fieldObject['inputType'] == 'checkbox') {
          if (this.filterFormData && this.filterFormData['applicationData']) {
            if (this.filterFormData['applicationData'][fieldName || ''] && this.filterFormData['applicationData'][fieldName || ''] != '') {
              fieldValueObject = this.filterFormData['applicationData'][fieldName || ''];
            }
          }
          const control: UntypedFormArray = this.buildFormArray(fieldObject, fieldValueObject);
          this.filterForm.addControl(fieldName || '', control);
        } else {
          const fieldValidation: ValidatorFn[] = [];
          if (fieldObject['mandatory']) {
            fieldValidation.push(Validators.required);
          }
          if (fieldObject['maxlength']) {
            fieldValidation.push(Validators.maxLength(fieldObject['maxlength']));
          }
          if (fieldObject['minlength']) {
            fieldValidation.push(Validators.minLength(fieldObject['minlength']));
          }
          if (fieldObject['regex']) {
            fieldValidation.push(Validators.pattern(fieldObject['regex']));
          }
          let fieldPAtchValue = '';
          if (this.filterFormData && this.filterFormData['applicationData']) {
            if (this.filterFormData['applicationData'][fieldName || ''] && this.filterFormData['applicationData'][fieldName || ''] != '') {
              fieldPAtchValue = this.filterFormData['applicationData'][fieldName || ''];
            }
          }
          const control: UntypedFormControl = new UntypedFormControl(fieldPAtchValue, fieldValidation as ValidatorFn[]);
          if (fieldObject['disable']) {
            control.disable();
          }
          this.filterForm.addControl(fieldName || '', control);
          if (fieldObject['formGroup']) {
            const control: UntypedFormControl = new UntypedFormControl('', []);
            this.filterForm.addControl(fieldName + '_formGroup', control);
          }
        }
      }
    }
  }

  buildFormArray(formArray: AnyType, buildFormArray?: AnyType) {
    const arr = formArray.options.map((option: AnyType) => {
      if (buildFormArray && buildFormArray.length > 0 && buildFormArray.includes(option.id)) {
        option.selected = true;
      }
      return new UntypedFormControl(option.selected ? option.id : null);
    });
    return new UntypedFormArray(arr);
  }

  filterData() {
    let filterData: AnyType = {};
    for (const key in this.filterForm.value) {
      filterData = {
        [key]: this.filterForm.value[key] && this.filterForm.value[key].name ? this.filterForm.value[key].name : this.filterForm.value[key],
        ...filterData,
      };
    }
    this.clickApplyFilter.emit(filterData);
    this.applyFilterCount.emit(this.commonUtilityLib.filterDataCount(filterData));
  }

  resetForm() {
    this.filterFormGroup();
    this.filterForm.reset({
      brand: '',
      client: '',
      assetType: '',
    });
    this.filterData();
  }

  onValueChange(event: AnyType, fieldObject: AnyType) {
    if (this.configObject === 'shareAssets') return;
    let selectedOption;
    const idx = fieldObject.options.findIndex((obj: AnyType) => obj._id == event.value._id);
    if (idx !== -1) {
      selectedOption = fieldObject.options[idx];
    }
    if (fieldObject['functionCall']) {
      const index = this.commonUtilityLib.findArrayIndex(this.filterFormData, 'brand');
      if (index !== -1) {
        this.filterFormData.formFields[index]['options'] = selectedOption['brands'];
      }
    }
  }
}
